import type { ApolloClient } from "@apollo/client";
import {
  IssueMovedDocument,
  IssueUpdatedDocument,
  IssueReorderedDocument,
  IssueArchivedDocument,
  IssueTrashedDocument,
  IssueRestoredDocument,
  IssueUnarchivedDocument,
  IssueListDataDocument,
  IssuesDocument,
  IssueListGroupCountsDocument,
} from "#app/lib/data";
import {
  defineSubscription,
  type AnySubscriptionRequest,
} from "#app/lib/subscriptions/subscription-provider";
import { writeIssueToCacheViaClient } from "#app/hooks/kanban/kanban-cache-utils";

// ---------------------------------------------------------------------------
// List-level realtime — reuses the kanban subscription documents (so the
// registry shares wire subs with a mounted board on the same workflow) but
// responds by writing issue fragments (instant field updates) and refetching
// list queries (structural changes like moves/archive/trash).
// ---------------------------------------------------------------------------

function refetchListQueries(client: ApolloClient) {
  void client.refetchQueries({
    include: [
      IssueListDataDocument,
      IssuesDocument,
      IssueListGroupCountsDocument,
    ],
  });
}

export function issueListSubscriptions(
  workflowId: string,
): AnySubscriptionRequest[] {
  const variables = { workflowId };
  return [
    // Issue fields updated — write fragment for instant in-place update
    defineSubscription({
      document: IssueUpdatedDocument,
      variables,
      onData: (client, data) => {
        const event = data.issueUpdated;
        if (!event) return;
        writeIssueToCacheViaClient(client, event.issue);
      },
    }),
    // Issue reordered within a stage — update sortOrder in cache and refetch
    // so the list reflects the new order.
    defineSubscription({
      document: IssueReorderedDocument,
      variables,
      onData: (client, data) => {
        const event = data.issueReordered;
        if (!event) return;
        writeIssueToCacheViaClient(client, event.issue);
        refetchListQueries(client);
      },
    }),
    // Structural changes — refetch list queries so rows appear/disappear correctly
    defineSubscription({
      document: IssueMovedDocument,
      variables,
      onData: (client, data) => {
        const event = data.issueMoved;
        if (!event) return;
        writeIssueToCacheViaClient(client, event.issue);
        refetchListQueries(client);
      },
    }),
    defineSubscription({
      document: IssueArchivedDocument,
      variables,
      onData: (client) => {
        refetchListQueries(client);
      },
    }),
    defineSubscription({
      document: IssueTrashedDocument,
      variables,
      onData: (client) => {
        refetchListQueries(client);
      },
    }),
    defineSubscription({
      document: IssueRestoredDocument,
      variables,
      onData: (client, data) => {
        const issue = data.issueRestored;
        if (!issue) return;
        writeIssueToCacheViaClient(client, issue);
        refetchListQueries(client);
      },
    }),
    defineSubscription({
      document: IssueUnarchivedDocument,
      variables,
      onData: (client, data) => {
        const issue = data.issueUnarchived;
        if (!issue) return;
        writeIssueToCacheViaClient(client, issue);
        refetchListQueries(client);
      },
    }),
  ];
}
